import BadgeTwoTone from "@mui/icons-material/BadgeTwoTone";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useVerifyCredentials } from "hooks/useVerifyCredentials";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { useEpcraBulkActionContext } from "./bulkActionContext";

const REFETCH = ["CredentialImplementationsForStaff", "CredentialImplementationForStaff"] as const;

export function BulkVerifyCredentialsMenuItem() {
  const { setDialogState, selectedRows } = useEpcraBulkActionContext();

  return (
    <MenuItem onClick={() => setDialogState("verify")} disabled={selectedRows.length === 0}>
      <ListItemIcon>
        <BadgeTwoTone fontSize="small" />
      </ListItemIcon>
      <ListItemText>Trigger verification…</ListItemText>
    </MenuItem>
  );
}

export function BulkVerifyCredentialsDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, onSuccess, selectedRows } = useEpcraBulkActionContext();
  const [verifyCredentials, { loading }] = useVerifyCredentials();

  const { eligibleFacilityIds, skippedNoFacility, skippedNoCredential } = useMemo(() => {
    let skippedNoFacility = 0;
    let skippedNoCredential = 0;
    const facilityIds = new Set<string>();
    for (const row of selectedRows) {
      if (!row.facilityId) {
        skippedNoFacility += 1;
        continue;
      }
      if (!row.credentialInEncamp) {
        skippedNoCredential += 1;
        continue;
      }
      facilityIds.add(row.facilityId);
    }
    return {
      eligibleFacilityIds: [...facilityIds],
      skippedNoFacility,
      skippedNoCredential,
    };
  }, [selectedRows]);

  const open = dialogState === "verify";

  const handleConfirm = useCallback(async () => {
    if (eligibleFacilityIds.length === 0) {
      alerts.error("No rows with a linked Encamp credential to verify.");
      onClose();
      return;
    }
    try {
      const res = await verifyCredentials({
        variables: {
          input: eligibleFacilityIds.map((facilityId) => ({ facilityId })),
        },
        refetchQueries: [...REFETCH],
      });
      const jobCount = res.data?.verifyCredentials.count ?? 0;
      const skipped = skippedNoFacility + skippedNoCredential;
      alerts.info(
        `Queued ${jobCount} verification ${pluralize("job", jobCount)} for ${eligibleFacilityIds.length} ${pluralize("facility", eligibleFacilityIds.length)}` +
          (skipped > 0 ? ` (${skipped} ${pluralize("row", skipped)} skipped).` : ".") +
          " Check back later for results."
      );
      onClose();
      onSuccess();
    } catch (e) {
      alerts.error("Could not queue credential verification", e);
    }
  }, [
    alerts,
    eligibleFacilityIds,
    onClose,
    onSuccess,
    skippedNoCredential,
    skippedNoFacility,
    verifyCredentials,
  ]);

  const skippedTotal = skippedNoFacility + skippedNoCredential;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      header="Trigger credential verification"
      loading={loading}
      onConfirm={() => handleConfirm()}
      info={
        <>
          <p>
            Queue credential verification for your {selectedRows.length} selected{" "}
            {pluralize("row", selectedRows.length)}. Facilities sharing a credential are verified
            together.
          </p>
          {skippedTotal > 0 ? (
            <p>
              <strong>{skippedTotal}</strong> {pluralize("row", skippedTotal)} will be skipped
              {skippedNoFacility > 0 && skippedNoCredential > 0
                ? ` (${skippedNoFacility} missing facility ID; ${skippedNoCredential} with no credential in Encamp).`
                : skippedNoFacility > 0
                  ? ` (${skippedNoFacility} missing facility ID).`
                  : ` (${skippedNoCredential} with no credential in Encamp).`}
            </p>
          ) : null}
          {eligibleFacilityIds.length > 0 ? (
            <p>
              <strong>{eligibleFacilityIds.length}</strong>{" "}
              {pluralize("facility", eligibleFacilityIds.length)} will be queued.
            </p>
          ) : (
            <p>No facilities will be queued.</p>
          )}
        </>
      }
      confirmText="Queue verification"
      cancelText="Cancel"
    />
  );
}
